import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../../../services/api";
import { mergeFollowUpDraft } from "../mergeFollowUp";
import { FOLLOW_UP_MERGE_FIELDS } from "../model/materielConstants";
import { resolveMergeConflict } from "../model/followUpRules";

const CONFIG = { noRedirectOn401: true };
const AUTOSAVE_DELAY_MS = 500;

// Lignes de suivi, contacts et couleurs de colonnes d'un service, chargés ensemble.
export function useFollowUpData(serviceKey) {
    const [rows, setRows] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [columnColors, setColumnColors] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const endpoint = `/medical-equipment/services/${serviceKey}/follow-ups`;
    const contactsEndpoint = `/medical-equipment/services/${serviceKey}/patient-contacts`;
    const colorsEndpoint = `/medical-equipment/services/${serviceKey}/column-colors`;

    const refresh = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([
            API.get(endpoint, CONFIG),
            API.get(contactsEndpoint, CONFIG),
            API.get(colorsEndpoint, CONFIG),
        ])
            .then(([followUpsResponse, contactsResponse, colorsResponse]) => {
                setRows(followUpsResponse.data);
                setContacts(contactsResponse.data);
                setColumnColors(colorsResponse.data || {});
            })
            .catch(() => setError("Impossible de charger le tableau de suivi."))
            .finally(() => setLoading(false));
    }, [endpoint, contactsEndpoint, colorsEndpoint]);

    useEffect(() => { refresh(); }, [refresh]);

    return {
        endpoint, contactsEndpoint, colorsEndpoint, rows, setRows, contacts, setContacts,
        columnColors, setColumnColors, loading, error, setError, refresh,
    };
}

// Modification concurrente (409) : la ligne est relue, les changements de chaque côté sont fusionnés et, si les
// deux côtés ont changé le même champ, l'utilisateur choisit la valeur à garder.
function useFollowUpMerge({ endpoint, setRows, setSavingId, setError, completeRowSave }) {
    const [mergeConflict, setMergeConflict] = useState(null);

    const handleConcurrentRowSave = async (id, base, local) => {
        const latestResponse = await API.get(endpoint, CONFIG);
        const remote = latestResponse.data.find(row => row.id === id);
        if (!remote) throw new Error("Cette ligne n'existe plus.");
        const { merged, conflicts } = mergeFollowUpDraft(base, local, remote, FOLLOW_UP_MERGE_FIELDS);
        setRows(prev => prev.map(row => row.id === id ? remote : row));
        if (conflicts.length > 0) {
            setMergeConflict({
                id, base, local, remote, merged, conflicts,
                choices: Object.fromEntries(conflicts.map(field => [field, "mine"])),
            });
            return;
        }
        try {
            const response = await API.patch(`${endpoint}/${id}`, merged, CONFIG);
            completeRowSave(id, response.data);
        } catch (requestError) {
            if (requestError.response?.status !== 409) throw requestError;
            await handleConcurrentRowSave(id, remote, merged);
        }
    };

    const confirmMerge = async () => {
        if (!mergeConflict) return;
        const { id, remote } = mergeConflict;
        const resolved = resolveMergeConflict(mergeConflict);
        setSavingId(id);
        setError(null);
        try {
            const response = await API.patch(`${endpoint}/${id}`, resolved, CONFIG);
            completeRowSave(id, response.data);
            setMergeConflict(null);
        } catch (requestError) {
            if (requestError.response?.status === 409) {
                setMergeConflict(null);
                await handleConcurrentRowSave(id, remote, resolved);
            } else {
                setError(requestError.response?.data?.message || "Impossible d'enregistrer la fusion.");
            }
        } finally {
            setSavingId(null);
        }
    };

    const chooseMerge = (field, choice) => setMergeConflict(current => ({
        ...current,
        choices: { ...current.choices, [field]: choice },
    }));

    const cancelMerge = () => {
        setRows(prev => prev.map(row => row.id === mergeConflict.id ? mergeConflict.remote : row));
        setMergeConflict(null);
    };

    return { mergeConflict, handleConcurrentRowSave, confirmMerge, chooseMerge, cancelMerge };
}

// Enregistrement automatique des lignes : chaque modification s'affiche tout de suite et part au serveur après une
// courte pause ; les modifications faites pendant un enregistrement partent ensuite, sur la ligne à jour.
export function useFollowUpSaving({ endpoint, rows, setRows, setError }) {
    const [savingId, setSavingId] = useState(null);
    const rowsRef = useRef(rows);
    const pendingRowSavesRef = useRef({});
    const rowSaveTimersRef = useRef({});
    const rowSaveInFlightRef = useRef({});

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);
    useEffect(() => () => {
        Object.values(rowSaveTimersRef.current).forEach(clearTimeout);
    }, []);

    const completeRowSave = (id, data) => {
        const waiting = pendingRowSavesRef.current[id];
        if (waiting) {
            waiting.base = data;
            waiting.local = { ...data, ...waiting.patch };
        }
        setRows(prev => prev.map(row => (
            row.id === id ? (waiting?.local || data) : row
        )));
    };

    const merge = useFollowUpMerge({ endpoint, setRows, setSavingId, setError, completeRowSave });

    const flushPendingRow = async id => {
        if (rowSaveInFlightRef.current[id]) {
            rowSaveTimersRef.current[id] = setTimeout(() => flushPendingRow(id), 100);
            return;
        }
        const pending = pendingRowSavesRef.current[id];
        if (!pending) return;
        delete pendingRowSavesRef.current[id];
        delete rowSaveTimersRef.current[id];
        rowSaveInFlightRef.current[id] = true;
        setSavingId(id);
        setError(null);
        try {
            const response = await API.patch(`${endpoint}/${id}`, pending.local, CONFIG);
            completeRowSave(id, response.data);
        } catch (requestError) {
            if (requestError.response?.status === 409) {
                await merge.handleConcurrentRowSave(id, pending.base, pending.local);
            } else {
                setRows(prev => prev.map(row => row.id === id ? pending.base : row));
                setError(requestError.response?.data?.message || "Impossible d'enregistrer la modification.");
            }
        } finally {
            rowSaveInFlightRef.current[id] = false;
            setSavingId(null);
            if (pendingRowSavesRef.current[id]) {
                rowSaveTimersRef.current[id] = setTimeout(() => flushPendingRow(id), 0);
            }
        }
    };

    const patchRow = (id, patch) => {
        const existing = pendingRowSavesRef.current[id];
        const current = existing?.local || rowsRef.current.find(row => row.id === id);
        if (!current) return;
        const next = { ...current, ...patch };
        setRows(prev => prev.map(row => row.id === id ? next : row));
        setError(null);
        pendingRowSavesRef.current[id] = {
            base: existing?.base || current,
            patch: { ...(existing?.patch || {}), ...patch },
            local: next,
        };
        clearTimeout(rowSaveTimersRef.current[id]);
        rowSaveTimersRef.current[id] = setTimeout(() => flushPendingRow(id), AUTOSAVE_DELAY_MS);
    };

    const deleteRow = async id => {
        clearTimeout(rowSaveTimersRef.current[id]);
        delete rowSaveTimersRef.current[id];
        delete pendingRowSavesRef.current[id];
        const previous = rows;
        setRows(prev => prev.filter(row => row.id !== id));
        try {
            await API.delete(`${endpoint}/${id}`, CONFIG);
        } catch {
            setRows(previous);
            setError("Impossible de supprimer la ligne.");
        }
    };

    return { savingId, setSavingId, patchRow, deleteRow, ...merge };
}
